import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createAirlineClass,
  getAirlineClassByID,
  getAllActiveAirlineClass,
  getAllAirlineClasses,
  updateAirlineClass,
} from "./services/airlineclass.services";
import { clearAirlineClassDetails } from "./airlineclassSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";

const CreateAirlineClass: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const AirlineClassDetail = useAppSelector(
    (state) => state.airlineclass.AirlineClassDetail
  );
  const AirlineClassID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.airlineclass.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    return () => {
      dispatch(clearAirlineClassDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (AirlineClassID) {
      dispatch(getAirlineClassByID(AirlineClassID));
    }
  }, [AirlineClassID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!AirlineClassID) {
        const insertPayload = {
          ClassName: values.ClassName,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createAirlineClass(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAirlineClassDetails());
          if (isIndirect) {
            dispatch(getAllActiveAirlineClass());
          } else {
            dispatch(getAllAirlineClasses());
          }
        }
      } else {
        const editPayload = {
          ID: AirlineClassID,
          ClassName: values.ClassName,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateAirlineClass(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAirlineClassDetails());
          dispatch(getAllAirlineClasses());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={AirlineClassDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="mui-drawer-header">
              <Typography.h4 className="m-0">
                {AirlineClassID ? "Update Airline Class" : "Add Airline Class"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="ClassName"
                label="Airline Class"
                placeholder="Airline Class"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                label={"Status"}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                placeholder="i.e. Active"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label="Save"
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 10 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateAirlineClass;
